import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search,
  MoreHorizontal,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPressReleases = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const statusTabs = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "published", label: "Published" },
    { value: "rejected", label: "Rejected" },
    { value: "draft", label: "Draft" },
  ];

  useEffect(() => {
    fetchPressReleases();
  }, [status, page]);

  const fetchPressReleases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "25" });
      if (status !== "all") params.set("status", status);
      if (search) params.set("search", search);

      const response = await fetch(`${API_URL}/api/admin/press-releases?${params}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPressReleases(data.items);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (prId, action, reason = null) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/press-releases/${prId}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action, reason }),
      });

      if (response.ok) {
        toast.success(`PR ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "updated"}`);
        fetchPressReleases();
      } else {
        toast.error("Action failed");
      }
    } catch (error) {
      toast.error("Error performing action");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      pending: "bg-amber-50 text-amber-700",
      scheduled: "bg-blue-50 text-blue-700",
      published: "bg-emerald-50 text-emerald-700",
      rejected: "bg-red-50 text-red-700",
      needs_revision: "bg-orange-50 text-orange-700",
    };
    return styles[status] || styles.draft;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Press Releases</h1>
        <p className="text-slate-600 mt-1">Review and manage all press release submissions</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {statusTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setStatus(tab.value);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    status === tab.value
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`admin-status-tab-${tab.value}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchPressReleases()}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>PR ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Submitted By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pressReleases.map((pr) => (
                  <TableRow
                    key={pr.pr_id}
                    className="cursor-pointer hover:bg-slate-50"
                    data-testid={`admin-pr-row-${pr.pr_id}`}
                  >
                    <TableCell className="font-medium">{pr.pr_id}</TableCell>
                    <TableCell
                      className="max-w-md truncate"
                      onClick={() => navigate(`/admin/press-releases/${pr.pr_id}`)}
                    >
                      {pr.title}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="text-slate-900">{pr.user?.name}</p>
                        <p className="text-slate-500 text-xs">{pr.user?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {format(new Date(pr.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusBadge(pr.status)} capitalize`}>
                        {pr.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/press-releases/${pr.pr_id}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {pr.status === "pending" && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleAction(pr.pr_id, "approve")}
                                className="text-emerald-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  const reason = prompt("Rejection reason:");
                                  if (reason) handleAction(pr.pr_id, "reject", reason);
                                }}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  const reason = prompt("Revision notes:");
                                  if (reason) handleAction(pr.pr_id, "request_revision", reason);
                                }}
                                className="text-amber-600"
                              >
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Request Revision
                              </DropdownMenuItem>
                            </>
                          )}
                          {pr.status === "published" && (
                            <DropdownMenuItem
                              onClick={() => {
                                const date = prompt("Schedule date (YYYY-MM-DD):");
                                if (date) handleAction(pr.pr_id, "schedule", null);
                              }}
                            >
                              <Clock className="h-4 w-4 mr-2" />
                              Reschedule
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPressReleases;
